import { z } from 'zod';

/**
 * The vocabulary of printing: the paper sizes, typefaces, alignments and field
 * keys a template is assembled from, with the labels the builder puts on them.
 *
 * Split from print-template.ts because these are what the *client* needs to
 * build a `<Select>` — the section schemas next door are what the server needs
 * to validate a document. Keeping the two apart means a screen that only offers
 * a page-size picker does not import the whole document schema.
 */

/**
 * The paper sizes offered.
 *
 * A subset of the ~40 pdfmake accepts, chosen because they are the ones a lab
 * actually loads: A4 and A5 (the region's default and its half-sheet), the
 * three North American sizes, and A3 for a wide multi-panel sheet. Offering
 * RA2 would be a longer list, not a better one.
 */
export const printPageSizeSchema = z.enum(['A3', 'A4', 'A5', 'B5', 'LETTER', 'LEGAL', 'FOLIO']);
export type PrintPageSize = z.infer<typeof printPageSizeSchema>;

export const PRINT_PAGE_SIZE_LABELS = {
  A3: 'A3 — 297 × 420 mm',
  A4: 'A4 — 210 × 297 mm',
  A5: 'A5 — 148 × 210 mm',
  B5: 'B5 — 176 × 250 mm',
  LETTER: 'Letter — 8.5 × 11 in',
  LEGAL: 'Legal — 8.5 × 14 in',
  FOLIO: 'Folio — 8.5 × 13 in',
} as const satisfies Record<PrintPageSize, string>;

export const printOrientationSchema = z.enum(['portrait', 'landscape']);

/**
 * The typeface.
 *
 * These three are the PDF standard fonts: every PDF reader and every PostScript
 * printer already has them, so they embed no bytes and cannot render as a
 * fallback face on someone else's machine. That is worth more on a clinical
 * document than a wider choice would be — a report that prints in a substituted
 * font on the one printer at the front desk is a defect the lab cannot fix.
 */
export const printFontSchema = z.enum(['Helvetica', 'Times', 'Courier']);
export type PrintFont = z.infer<typeof printFontSchema>;

export const PRINT_FONT_LABELS = {
  Helvetica: 'Helvetica — sans serif',
  Times: 'Times — serif',
  Courier: 'Courier — monospace',
} as const satisfies Record<PrintFont, string>;

export const printAlignmentSchema = z.enum(['left', 'center', 'right']);
export type PrintAlignment = z.infer<typeof printAlignmentSchema>;

/** How much of the results table is ruled. */
export const printGridSchema = z.enum(['none', 'horizontal', 'all']);
export type PrintGrid = z.infer<typeof printGridSchema>;

/**
 * `none` keeps the rules above and below the heading row and drops the rest.
 * A results table with no rule at all under its headings reads as a list of
 * loose values, so "none" here means "no row rules", and the label says so.
 */
export const PRINT_GRID_LABELS = {
  none: 'Heading rule only',
  horizontal: 'Row rules',
  all: 'Full grid',
} as const satisfies Record<PrintGrid, string>;

/**
 * Which pages carry the full letterhead.
 *
 * `every` repeats it; `first` prints it once and gives later pages a one-line
 * continuation strip naming the patient. The band is reserved on every page
 * either way — a PDF's page margins are fixed for the whole document — so
 * `first` buys a cleaner page rather than more room on it.
 */
export const printHeaderRepeatSchema = z.enum(['every', 'first']);

export const printFooterRepeatSchema = z.enum(['every', 'last']);

/** How one test follows another. `same_page` still never splits a test that fits a page. */
export const printTestFlowSchema = z.enum(['new_page', 'same_page']);
export type PrintTestFlow = z.infer<typeof printTestFlowSchema>;

/**
 * How the letterhead arranges its three parts.
 *
 * `compact` puts the mark, the patient's details and the barcode on one row and
 * rules underneath — the arrangement this lab's stationery has always used, and
 * the one that costs the least vertical space, which on a two-page report is a
 * page.
 *
 * `stacked` gives the mark a row of its own alongside the lab's name and
 * address, and puts the patient's details on a second row. Taller, but the only
 * arrangement with room for a printed letterhead address.
 */
export const printHeaderLayoutSchema = z.enum(['compact', 'stacked']);
export type PrintHeaderLayout = z.infer<typeof printHeaderLayoutSchema>;

export const PRINT_HEADER_LAYOUT_LABELS = {
  compact: 'Compact — details beside the mark',
  stacked: 'Stacked — details on their own row',
} as const satisfies Record<PrintHeaderLayout, string>;

/** Which identifier the barcode carries. */
export const barcodeSourceSchema = z.enum(['pin_no', 'mr_no']);

/**
 * A field in the patient identity block.
 *
 * `reported_at` is the moment the PDF was produced, not a stored column — a
 * report has no "reported" timestamp, and the previous printer put today's date
 * on every reprint of every report. It stays for continuity with the paper
 * people are used to, but it is labelled `Printed` by default rather than
 * `Reported`, because that is what it is.
 */
export const patientFieldKeySchema = z.enum([
  'name',
  'age',
  'gender',
  'pin_no',
  'mr_no',
  'phone_number',
  'refer_by',
  'visit_date',
  'reported_at',
  'payment_status',
]);

/**
 * A column in the results table.
 *
 * The analyte, its result and the H/L mark, then one column per field of a
 * Normal Range (`ParameterValue`) — each printed from the snapshot on the
 * report, so every field the lab can define can also be printed.
 */
export const resultColumnKeySchema = z.enum([
  'parameter',
  'result',
  'flag',
  'unit',
  'reference',
  'age',
  'age_group',
  'gender',
  'other',
]);
type ResultColumnKey = z.infer<typeof resultColumnKeySchema>;

export const RESULT_COLUMN_DESCRIPTIONS = {
  parameter: 'The analyte name',
  result: 'The entered value',
  flag: 'H / L against the reference range',
  unit: 'The unit recorded with the result',
  reference: 'The reference range recorded with the result',
  age: 'The age the reference range applies to',
  age_group: 'The age group the reference range applies to',
  gender: 'The gender the reference range applies to',
  other: 'Any other note recorded with the reference range',
} as const satisfies Record<ResultColumnKey, string>;

/**
 * The columns that come from the reference range.
 *
 * Each is left out of a test's table when no row of that test has a value for
 * it — a lab that never records an age group should not print an empty column.
 */
const RANGE_COLUMN_KEYS = [
  'unit',
  'reference',
  'age',
  'age_group',
  'gender',
  'other',
] as const satisfies readonly ResultColumnKey[];
type RangeColumnKey = (typeof RANGE_COLUMN_KEYS)[number];

export function isRangeColumn(key: ResultColumnKey): key is RangeColumnKey {
  return (RANGE_COLUMN_KEYS as readonly ResultColumnKey[]).includes(key);
}

/**
 * Every results column as a template starts with it, in print order.
 *
 * Also what a stored template is completed from: a column missing from its
 * list is appended from here, switched on. Widths are millimetres and sum past
 * A4 with every column on — the renderer scales fixed widths to fit, and the
 * range columns with nothing in them are dropped before it does.
 */
export const RESULT_COLUMN_DEFAULTS = [
  { key: 'parameter', label: 'Parameter', width: '*', align: 'left' },
  { key: 'result', label: 'Result', width: 30, align: 'left' },
  // No heading on purpose: a one-character "H" under the word "Flag" reads as
  // another data column, and the mark only means something beside its number.
  { key: 'flag', label: '', width: 8, align: 'center' },
  { key: 'unit', label: 'Unit', width: 22, align: 'left' },
  { key: 'reference', label: 'Reference range', width: 44, align: 'left' },
  { key: 'age', label: 'Age', width: 16, align: 'left' },
  { key: 'age_group', label: 'Age group', width: 20, align: 'left' },
  { key: 'gender', label: 'Gender', width: 16, align: 'left' },
  { key: 'other', label: 'Other', width: 24, align: 'left' },
] as const satisfies readonly {
  key: ResultColumnKey;
  label: string;
  width: '*' | number;
  align: PrintAlignment;
}[];

/**
 * A results column as stored — the shape `resultColumnSchema` parses to.
 *
 * A type alias rather than an interface: the document is written to a Prisma
 * JSON column, and only an alias is assignable to its index signature.
 */
type StoredResultColumn = {
  key: ResultColumnKey;
  label: string;
  width: '*' | 'auto' | number;
  align: PrintAlignment;
  enabled: boolean;
};

/**
 * A template's columns with every key present exactly once.
 *
 * Keeps the stored order and settings, drops a repeated key, and appends any
 * key the template does not list yet from `RESULT_COLUMN_DEFAULTS`, switched
 * on — so a column added to the vocabulary is shown on every template without
 * anyone having to edit it, and can then be hidden from the builder.
 */
export function withEveryResultColumn(
  columns: readonly StoredResultColumn[],
): StoredResultColumn[] {
  const byKey = new Map<ResultColumnKey, StoredResultColumn>();
  for (const column of columns) if (!byKey.has(column.key)) byKey.set(column.key, column);
  for (const column of RESULT_COLUMN_DEFAULTS) {
    if (!byKey.has(column.key)) byKey.set(column.key, { ...column, enabled: true });
  }
  return [...byKey.values()];
}

/* ------------------------------------------------------------------------- *
 * Signature blocks
 * ------------------------------------------------------------------------- */

/**
 * The three people a report can be attributed to.
 *
 * A closed vocabulary, like every other key in this file: the builder renders a
 * control per member, and the renderer resolves each one from a named field on
 * the report. Adding a fourth means adding a column, not a string.
 */
export const signatureBlockKeySchema = z.enum(['performed_by', 'verified_by', 'consultant']);
export type SignatureBlockKey = z.infer<typeof signatureBlockKeySchema>;

export const SIGNATURE_BLOCK_DESCRIPTIONS = {
  performed_by: 'The staff member who entered the results',
  verified_by: 'The technologist who approved the report',
  consultant: 'The reporting doctor recorded on this report',
} as const satisfies Record<SignatureBlockKey, string>;

export const SIGNATURE_BLOCK_LABELS = {
  performed_by: 'Report created by',
  verified_by: 'Approved by',
  consultant: 'Reported by',
} as const satisfies Record<SignatureBlockKey, string>;
